import { NextPage } from 'next';
import { ParsedUrlQuery } from 'querystring';
import { useLoadAnalytics } from '@sy/app-client';

import BlogTemplate from '~/components/templates/BlogTemplate';
import BlogOverview from '~/scenes/Blog/scenes/BlogOverview';
import {
	getAllBlogPostTilesSortedByCreation,
	getCategorizedBlogPostTilesSortedByCreation,
	getBlogCategory,
	getBlogMainCategory,
} from '~/services/blog';
import { buildGetServerSideProps } from '~/getServerSideProps';

import type { CommonContext, InferProps } from '~/getServerSideProps';
import type { BlogMainCategory } from '~/services/blog/categories';
import type { CacheReducedBlogPost } from '~/services/contentful/model/ContentfulBlogPost';
import buildMetatadata from '~/utils/buildMetadata';

const PostsGroup: NextPage<InferProps<typeof getServerSideProps>> = ({
	category,
	posts,
	user,
}) => {
	useLoadAnalytics();

	return (
		<BlogTemplate activeCategory={category} user={user}>
			<BlogOverview posts={posts} category={category} />
		</BlogTemplate>
	);
};

interface UrlParams extends ParsedUrlQuery {
	categoryUrl?: string[];
}

const categorizedPostsServerSideProps = async (
	context: CommonContext<UrlParams>,
) => {
	const { locale, params, query } = context;
	const isPreview = !!query?.preview;

	const category = getBlogCategory(params?.categoryUrl?.[0]);
	const mainCategory = getBlogMainCategory(category);

	if (!category || category !== mainCategory) {
		return {
			redirect: {
				destination: '/blog',
				permanent: false,
			},
		};
	}

	return {
		props: {
			category,
			posts: await getCategorizedBlogPostTilesSortedByCreation({
				locale,
				category,
				isPreview,
			}),
		},
	};
};

const allPostsServerSideProps = async (context: CommonContext<UrlParams>) => {
	const { locale, query } = context;
	const isPreview = !!query?.preview;

	return {
		props: {
			posts: await getAllBlogPostTilesSortedByCreation({
				locale,
				isPreview,
			}),
			metadata: buildMetatadata({}),
		},
	};
};

const customServerSideProps = async (context: CommonContext<UrlParams>) => {
	const { params, query } = context;

	const isPreview = !!query?.preview;

	// TODO support multiple locales, we force it to english for now until we have translated blog posts,
	// english is the only locale we have in blogs today so this give us feature parity
	// we support multi locale for preview only
	const locale = isPreview ? context.locale : 'en';

	if (params?.categoryUrl) {
		return categorizedPostsServerSideProps({ ...context, locale });
	}

	return allPostsServerSideProps({ ...context, locale });
};

export default PostsGroup;

export const getServerSideProps = buildGetServerSideProps<{
	category?: BlogMainCategory;
	posts: CacheReducedBlogPost[] | null;
}>({
	customServerSideProps,
});
